import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { LogoMark } from "./Logo";
import { LeadForm } from "./LeadForm";
import { API, waLink, RENTAL_BUDGET_OPTIONS } from "../lib/site";

const LeadCtx = createContext({ openLead: () => {} });

export const useLead = () => useContext(LeadCtx);

export const LeadProvider = ({ children }) => {
  const [lead, setLead] = useState(null);

  const openLead = (opts = {}) =>
    setLead({
      title: opts.title || "Get Instant Callback & Price List",
      subtitle: opts.subtitle || "Share your details — our senior property advisor responds within 30 minutes.",
      project: opts.project || "",
      interest: opts.interest || "",
      intent: opts.intent || "",
      source: opts.source || "Popup",
      brochureListingId: opts.brochureListingId || "",
      rental: opts.rental || false,
    });

  const handleSuccess = () => {
    if (lead.intent === "brochure") {
      if (lead.brochureListingId) {
        window.open(`${API}/brochures/${lead.brochureListingId}`, "_blank");
      } else if (lead.project) {
        window.open(waLink(`Hi Estate Paradise, please share the brochure and price list for ${lead.project}.`), "_blank");
      }
    }
    setTimeout(() => setLead(null), 900);
  };

  const submitLabel =
    lead?.intent === "brochure"
      ? lead.brochureListingId
        ? "Register & Download Brochure"
        : "Get Brochure on WhatsApp"
      : "Request Callback";

  return (
    <LeadCtx.Provider value={{ openLead }}>
      {children}
      <AnimatePresence>
        {lead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-end justify-center bg-navy-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setLead(null)}
            data-testid="lead-popup-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto bg-white shadow-[0_40px_90px_rgb(8,16,39,0.4)]"
              data-testid="lead-popup"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between bg-navy-900 px-7 py-5">
                <div className="flex items-center gap-3">
                  <LogoMark className="h-9 w-auto" dark />
                  <div>
                    <p className="font-serif text-lg font-medium text-white">{lead.title}</p>
                    {lead.project && (
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gold-400">{lead.project}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setLead(null)}
                  data-testid="lead-popup-close-btn"
                  aria-label="Close popup"
                  className="flex h-9 w-9 items-center justify-center border border-white/20 text-white/70 transition-colors duration-300 hover:border-gold-500 hover:text-gold-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="px-7 py-7">
                <p className="mb-6 text-sm leading-relaxed text-gray-500">{lead.subtitle}</p>
                <LeadForm
                  tid="lead-popup-form"
                  source={lead.source}
                  project={lead.project}
                  defaultInterest={lead.interest}
                  intent={lead.intent}
                  showMessage={false}
                  submitLabel={submitLabel}
                  budgetOptions={lead.rental ? RENTAL_BUDGET_OPTIONS : undefined}
                  budgetLabel={lead.rental ? "Preferred Monthly Rent" : undefined}
                  onSuccess={handleSuccess}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LeadCtx.Provider>
  );
};
